import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "warehouse", label: "Warehouse" },
  { id: "rikshaws", label: "Per rikshaw" },
  { id: "ledger", label: "Ledger" },
];

export default function AdminInventory() {
  const [tab, setTab] = useState("warehouse");

  return (
    <div className="space-y-5" data-testid="admin-inventory">
      <div>
        <span className="label-overline text-jow-gold">Stock</span>
        <h1 className="font-display text-3xl text-jow-navy mt-1">Inventory</h1>
      </div>

      <div className="inline-flex border border-jow-navy/15 bg-white">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest ${
              tab === t.id ? "bg-jow-navy text-jow-cream" : "text-jow-navy/60"
            }`}
            data-testid={`inv-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "warehouse" && <WarehouseTab />}
      {tab === "rikshaws" && <RikshawsTab />}
      {tab === "ledger" && <LedgerTab />}
    </div>
  );
}

function WarehouseTab() {
  const [items, setItems] = useState([]);
  const [low, setLow] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    if (low) params.set("low_stock", "true");
    if (q) params.set("q", q);
    api.get(`/admin/inventory/warehouse?${params.toString()}`).then((r) => setItems(r.data.items));
  }, [low, q]);

  return (
    <div className="space-y-3" data-testid="inv-warehouse">
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search SKU / name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[240px] border border-jow-navy/15 px-3 py-2 text-sm bg-white"
          data-testid="wh-search"
        />
        <label className="text-sm flex items-center gap-2 text-jow-navy/70">
          <input
            type="checkbox"
            checked={low}
            onChange={(e) => setLow(e.target.checked)}
            data-testid="wh-low"
          />
          Low stock only (≤ 5)
        </label>
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">Product</th>
            <th className="text-left px-4 py-3">SKU</th>
            <th className="text-right px-4 py-3">Warehouse</th>
            <th className="text-right px-4 py-3">On rikshaws</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={r.sku}
              className="border-t border-jow-navy/5"
              data-testid={`wh-row-${r.sku}`}
            >
              <td className="px-4 py-3 flex items-center gap-3">
                {r.image && <img src={r.image} alt="" className="w-9 h-9 object-cover" />}
                <div>
                  <p className="text-jow-navy">{r.product_name}</p>
                  <p className="text-[10px] text-jow-navy/50">{r.attrs?.color} · {r.attrs?.size}</p>
                </div>
              </td>
              <td className="px-4 py-3 font-mono text-xs">{r.sku}</td>
              <td
                className={`px-4 py-3 text-right font-display ${r.stock_warehouse <= 5 ? "text-jow-pink" : "text-jow-navy"}`}
              >
                {r.stock_warehouse <= 5 && <AlertTriangle size={12} className="inline mr-1" />}
                {r.stock_warehouse}
              </td>
              <td className="px-4 py-3 text-right text-jow-navy/70">{r.on_rikshaws}</td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center py-8 text-jow-navy/50">
                No rows.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RikshawsTab() {
  const [items, setItems] = useState([]);
  const [transfer, setTransfer] = useState({ sku: "", qty: 1, direction: "load", rikshaw_id: "" });

  const load = () =>
    api.get("/admin/inventory/rikshaw-summary").then((r) => setItems(r.data.items || []));

  useEffect(() => {
    load();
  }, []);

  const runTransfer = async () => {
    if (!transfer.sku || !transfer.rikshaw_id) {
      toast.error("SKU + Rikshaw required");
      return;
    }
    try {
      await api.post("/admin/inventory/transfer", transfer);
      toast.success(`Transferred ${transfer.qty} × ${transfer.sku}`);
      setTransfer((t) => ({ ...t, sku: "", qty: 1 }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="inv-rikshaws">
      {/* Transfer form */}
      <div className="bg-white border border-jow-gold/40 p-4" data-testid="inv-transfer-form">
        <p className="label-overline text-jow-navy mb-2">Admin transfer</p>
        <div className="grid grid-cols-5 gap-2">
          <input
            value={transfer.sku}
            placeholder="SKU"
            onChange={(e) => setTransfer({ ...transfer, sku: e.target.value })}
            className="border border-jow-navy/15 px-3 py-2 text-sm"
            data-testid="tr-sku"
          />
          <select
            value={transfer.rikshaw_id}
            onChange={(e) => setTransfer({ ...transfer, rikshaw_id: e.target.value })}
            className="border border-jow-navy/15 px-3 py-2 text-sm bg-white"
            data-testid="tr-rikshaw"
          >
            <option value="">— rikshaw —</option>
            {items.map((r) => (
              <option key={r.rikshaw.id} value={r.rikshaw.id}>
                {r.rikshaw.code}
              </option>
            ))}
          </select>
          <select
            value={transfer.direction}
            onChange={(e) => setTransfer({ ...transfer, direction: e.target.value })}
            className="border border-jow-navy/15 px-3 py-2 text-sm bg-white"
            data-testid="tr-direction"
          >
            <option value="load">Load (WH→Rikshaw)</option>
            <option value="unload">Unload (Rikshaw→WH)</option>
          </select>
          <input
            type="number"
            min={1}
            value={transfer.qty}
            onChange={(e) => setTransfer({ ...transfer, qty: Number(e.target.value) })}
            className="border border-jow-navy/15 px-3 py-2 text-sm"
            data-testid="tr-qty"
          />
          <button onClick={runTransfer} className="btn-primary-jow text-[11px]" data-testid="tr-apply">
            Transfer
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {items.map((r) => (
          <div
            key={r.rikshaw.id}
            className="bg-white border border-jow-navy/10 p-4"
            data-testid={`rik-card-${r.rikshaw.code}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-xl text-jow-navy">{r.rikshaw.code}</p>
                <p className="text-[10px] uppercase tracking-widest text-jow-navy/50">
                  {r.rikshaw.city} · {r.rikshaw.status}
                </p>
              </div>
              <span className="font-display text-3xl text-jow-gold">{r.total}</span>
            </div>
            {r.top_skus.length > 0 && (
              <ul className="mt-3 space-y-1">
                {r.top_skus.map((s) => (
                  <li key={s.sku} className="text-xs flex justify-between">
                    <span className="text-jow-navy/70 truncate">{s.name}</span>
                    <span className="text-jow-navy">{s.qty}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LedgerTab() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sku, setSku] = useState("");

  useEffect(() => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (sku) params.set("sku", sku);
    api.get(`/admin/inventory/ledger?${params.toString()}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [page, sku]);

  return (
    <div className="space-y-3" data-testid="inv-ledger">
      <div className="flex gap-2 items-center">
        <input
          type="text"
          placeholder="Filter by SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          className="flex-1 max-w-xs border border-jow-navy/15 px-3 py-2 text-sm"
          data-testid="ledger-sku"
        />
        <span className="text-xs text-jow-navy/55">{total} entries</span>
      </div>
      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">When</th>
            <th className="text-left px-4 py-3">SKU</th>
            <th className="text-left px-4 py-3">From → To</th>
            <th className="text-right px-4 py-3">Qty</th>
            <th className="text-left px-4 py-3">Note</th>
          </tr>
        </thead>
        <tbody>
          {items.map((l) => (
            <tr key={l.id} className="border-t border-jow-navy/5" data-testid={`ledger-${l.id}`}>
              <td className="px-4 py-2 text-xs text-jow-navy/70">{new Date(l.at).toLocaleString()}</td>
              <td className="px-4 py-2 font-mono text-xs">{l.sku}</td>
              <td className="px-4 py-2 text-xs text-jow-navy/80">
                {l.from} <ArrowDown size={10} className="inline text-jow-gold" /> {l.to}
              </td>
              <td className="px-4 py-2 text-right font-display">{l.qty}</td>
              <td className="px-4 py-2 text-xs text-jow-navy/55 truncate">{l.note}</td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center py-8 text-jow-navy/50">
                No movements.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="flex items-center justify-between text-xs text-jow-navy/55">
        <span>
          Page {page} of {Math.max(1, Math.ceil(total / 50))}
        </span>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
          >
            Prev
          </button>
          <button
            disabled={page * 50 >= total}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
